import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';

import { Field, reduxForm, SubmissionError } from 'redux-form';

import TextField from 'components/fields/TextField';
import FormError from 'components/common/FormError';
import AutosuggestField from 'components/fields/AutosuggestField';
import CheckboxField from 'components/fields/CheckboxField';

import {
  createWorkflowStatus,
  updateWorkflowStatus,
  requestFetchWorkflowStatuses,
  requestSearchWorkflowStatuses,
} from 'redux/modules/workflowStatuses';

import {
  requestFetchWorkflowStates,
  requestSearchWorkflowStates,
} from 'redux/modules/workflowStates';

import { accountLoginFail } from 'redux/modules/account';

import * as validate from 'utils/validate';

import objectDiff from 'utils/objectDiff';

const styles = () => ({
  grid: {
    marginTop: 10,
    '&:first-child': {
      marginTop: 0,
    },
  },
});

function onSubmit(values, dispatch, props) {
  const normalizeValues = (formValues) => {
    const normalizedValues = Object.assign({}, formValues);

    delete normalizedValues['Workflow State Name'];
    delete normalizedValues['Selected Workflow State Name'];
    delete normalizedValues['Next Workflow Status Name'];
    delete normalizedValues['Selected Next Workflow Status Name'];

    return normalizedValues;
  };
  if (props.ID) {
    if (props.pristine) {
      throw new SubmissionError({
        _error: 'Nothing to submit',
      });
    }
    const diffValues = objectDiff(values, props.initialValues);
    return new Promise((resolve, reject) => {
      dispatch(updateWorkflowStatus({
        values: {
          ID: props.ID,
          ...normalizeValues(diffValues),
        },
        resolve,
        reject,
      }));
    }).then(res => res,
      (err) => {
        throw new SubmissionError({ _error: err.description.indexOf('Unique check fail') !== -1
          ? 'Sorry, but such Workflow Status already exists' : err.description });
      });
  }

  return new Promise((resolve, reject) => {
    dispatch(createWorkflowStatus({
      values: normalizeValues(values),
      resolve,
      reject,
    }));
  }).then(res => res,
    (err) => {
      throw new SubmissionError({ _error: err.description.indexOf('Unique check fail') !== -1
        ? 'Sorry, but such Workflow Status already exists' : err.description });
    });
}

function onChange(values, dispatch, props) {
  if (values['Workflow State ID']
    && values['Selected Workflow State Name'] !== values['Workflow State Name']) {
    dispatch(props.change('Workflow State ID', null));
  }

  if (values['Next Workflow Status ID']
    && values['Selected Next Workflow Status Name'] !== values['Next Workflow Status Name']) {
    dispatch(props.change('Next Workflow Status ID', null));
  }
}

const validateForm = (values) => {
  const errors = {};

  if (!values['Workflow State ID']) {
    errors['Workflow State Name'] = 'Please, select Workflow State from list';
  }

  return errors;
};

const WorkflowStatusCreateEditForm = ({
  handleSubmit,
  className,
  error,
  change,
  actions,
  classes,
}) => (
  <form
    noValidate
    autoComplete="off"
    onSubmit={handleSubmit}
    className={className}
  >
    {error &&
      <Grid
        className={classes.grid}
        container
        spacing={24}
      >
        <Grid
          item
          xs={12}
        >
          <FormError>
            {error}
          </FormError>
        </Grid>
      </Grid>
    }
    <Grid
      className={classes.grid}
      container
      spacing={24}
    >
      <Grid
        item
        xs={12}
      >
        <Field
          id="Name"
          name="Name"
          label="Status Name"
          component={TextField}
          validate={[validate.required]}
          errorPlacement="bottom"
          fullWidth
        />
      </Grid>
    </Grid>
    <Grid
      className={classes.grid}
      container
      spacing={24}
    >
      <Grid
        item
        xs={12}
      >
        <Field
          id="Workflow State Name"
          name="Workflow State Name"
          label="Workflow Category"
          component={AutosuggestField}
          errorPlacement="top"
          fullWidth
          renderSuggestionContent={suggestion => suggestion.Name}
          onSuggestionSelected={(suggestion) => {
            change('Workflow State ID', suggestion.ID);
            change('Selected Workflow State Name', suggestion.Name);
            return suggestion.Name;
          }}
          fetchSuggestions={({ value, limit, offset }) => (
            value.length > 0
              ? requestSearchWorkflowStates({
                filters: [
                  {
                    name: 'Name',
                    operation: 'contains',
                    val: value,
                    case_sensitivity: false,
                  },
                ],
                limit,
                offset,
              }).then(res => res.data,
                (err) => {
                  if (err.data.error === 'Need authorisation') {
                    actions.accountLoginFail();
                  }
                  return err;
                })
              : requestFetchWorkflowStates({
                limit,
                offset,
              }).then(res => res.data,
                (err) => {
                  if (err.data.error === 'Need authorisation') {
                    actions.accountLoginFail();
                  }
                  return err;
                })
          )}
        />
      </Grid>
    </Grid>
    <Grid
      className={classes.grid}
      container
      spacing={24}
    >
      <Grid
        item
        xs={12}
      >
        <Field
          id="Next Workflow Status Name"
          name="Next Workflow Status Name"
          label="Auto Move to the following Status"
          component={AutosuggestField}
          errorPlacement="top"
          fullWidth
          renderSuggestionContent={suggestion => suggestion.Name}
          onSuggestionSelected={(suggestion) => {
            change('Next Workflow Status ID', suggestion.ID);
            change('Selected Next Workflow Status Name', suggestion.Name);
            return suggestion.Name;
          }}
          fetchSuggestions={({ value, limit, offset }) => (
            value.length > 0
              ? requestSearchWorkflowStatuses({
                filters: [
                  {
                    name: 'Name',
                    operation: 'contains',
                    val: value,
                    case_sensitivity: false,
                  },
                ],
                limit,
                offset,
              }).then(res => res.data,
                (err) => {
                  if (err.data.error === 'Need authorisation') {
                    actions.accountLoginFail();
                  }
                  return err;
                })
              : requestFetchWorkflowStatuses({
                limit,
                offset,
              }).then(res => res.data,
                (err) => {
                  if (err.data.error === 'Need authorisation') {
                    actions.accountLoginFail();
                  }
                  return err;
                })
          )}
        />
      </Grid>
    </Grid>
    <Grid
      className={classes.grid}
      container
      spacing={24}
    >
      <Grid
        item
        xs={12}
      >
        <Field
          id="Description"
          name="Description"
          label="Status Description"
          component={TextField}
          errorPlacement="bottom"
          fullWidth
        />
      </Grid>
    </Grid>
    <Grid
      container
      justify="flex-start"
      spacing={24}
    >
      <Grid
        item
        xs={12}
      >
        <Field
          id="Is Default"
          name="Is Default"
          label="Default Status in Workflow Category"
          component={CheckboxField}
        />
      </Grid>
    </Grid>
    <Grid
      container
      justify="flex-start"
      spacing={24}
    >
      <Grid
        item
        xs={12}
      >
        <Field
          id="Is Delivered"
          name="Is Delivered"
          label="Leave Footprint in Schedule"
          component={CheckboxField}
        />
      </Grid>
    </Grid>
  </form>
);

WorkflowStatusCreateEditForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  change: PropTypes.func.isRequired,
  error: PropTypes.string,
  className: PropTypes.string,
  classes: PropTypes.shape({
    grid: PropTypes.string.isRequired,
  }).isRequired,
  actions: PropTypes.shape({
    accountLoginFail: PropTypes.func.isRequired,
  }).isRequired,
};

WorkflowStatusCreateEditForm.defaultProps = {
  className: '',
  error: null,
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ accountLoginFail }, dispatch),
});

export default connect(null, mapDispatchToProps)(reduxForm({
  form: 'workflowStatusCreate',
  onSubmit,
  onChange,
  validate: validateForm,
})(withStyles(styles)(WorkflowStatusCreateEditForm)));



// WEBPACK FOOTER //
// ./src/components/settings/WorkflowStatusCreateEditDialog/WorkflowStatusCreateEditForm.js